import { useQuery } from '@apollo/client';
import { formatDistanceToNowStrict } from 'date-fns';
import { GET_ACCOUNT_AGENTS } from '../../graphql';

/** An agent that can act on the site (apiserver platform/services/accountAgents.js). */
export interface AccountAgent {
  id: string;
  name: string;
  via: string;
  keyPrefix?: string | null;
  connectedBy?: string | null;
  connectedAt: string;
  lastActiveAt?: string | null;
  active: boolean;
}

/** Team and Connections read the same list, so they never disagree about who's connected. */
export function useAccountAgents(accountId?: string | null) {
  const { data, loading } = useQuery<{ accountAgents: AccountAgent[] }>(GET_ACCOUNT_AGENTS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  return { agents: data?.accountAgents || [], loading };
}

/** "2 hours ago", or "Joined Jul 2026" / "Never" when there's no activity to show. */
export function lastActiveLabel(at?: string | null, joined?: string | null): string {
  const d = at ? new Date(at) : null;
  if (d && !Number.isNaN(d.getTime())) return formatDistanceToNowStrict(d, { addSuffix: true });
  const j = joined ? new Date(joined) : null;
  if (j && !Number.isNaN(j.getTime()))
    return `Joined ${j.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
  return 'Never';
}

/** "Agent · Remote MCP · connected by htran": the line under an agent's name. */
export function agentLine(a: AccountAgent): string {
  return [
    'Agent',
    a.via,
    a.connectedBy ? `connected by ${a.connectedBy.split('@')[0]}` : '',
    a.active ? '' : 'turned off',
  ]
    .filter(Boolean)
    .join(' · ');
}
